import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, User, Building2, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const DJANGO_BASE_URL = 'https://agritrack.samperlabs.com';

export const Register = () => {
  const { locale } = useI18n();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    farmName: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const translations = {
    en: {
      createAccount: 'Create your account',
      subtitle: 'Start your free trial. No credit card required.',
      nameLabel: 'Full name',
      namePlaceholder: 'John Farmer',
      emailLabel: 'Email address',
      emailPlaceholder: 'you@farm.com',
      farmLabel: 'Farm / Organization name',
      farmPlaceholder: 'Green Valley Ranch',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Create a password',
      confirmPasswordLabel: 'Confirm password',
      confirmPasswordPlaceholder: 'Confirm your password',
      passwordMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 8 characters',
      createBtn: 'Create Account',
      orContinueWith: 'or sign up with',
      googleSignUp: 'Continue with Google',
      haveAccount: 'Already have an account?',
      signIn: 'Sign in',
      freeForever: 'Free forever for up to 50 animals',
      terms: 'By creating an account, you agree to our',
      termsLink: 'Terms of Service',
      and: 'and',
      privacyLink: 'Privacy Policy',
    },
    tr: {
      createAccount: 'Hesabınızı oluşturun',
      subtitle: 'Ücretsiz denemenizi başlatın. Kredi kartı gerekmez.',
      nameLabel: 'Ad Soyad',
      namePlaceholder: 'Ahmet Çiftçi',
      emailLabel: 'E-posta adresi',
      emailPlaceholder: 'siz@ciftlik.com',
      farmLabel: 'Çiftlik / Kuruluş adı',
      farmPlaceholder: 'Yeşil Vadi Çiftliği',
      passwordLabel: 'Şifre',
      passwordPlaceholder: 'Şifre oluşturun',
      confirmPasswordLabel: 'Şifre tekrarı',
      confirmPasswordPlaceholder: 'Şifrenizi tekrar girin',
      passwordMismatch: 'Şifreler eşleşmiyor',
      passwordTooShort: 'Şifre en az 8 karakter olmalı',
      createBtn: 'Hesap Oluştur',
      orContinueWith: 'veya şununla kaydolun',
      googleSignUp: 'Google ile Devam Et',
      haveAccount: 'Zaten hesabınız var mı?',
      signIn: 'Giriş yap',
      freeForever: '50 hayvana kadar sonsuza dek ücretsiz',
      terms: 'Hesap oluşturarak, kabul etmiş olursunuz',
      termsLink: 'Hizmet Şartları',
      and: 've',
      privacyLink: 'Gizlilik Politikası',
    },
  };

  const text = translations[locale] || translations.en;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    const newErrors = {};
    if (!formData.name) newErrors.name = locale === 'tr' ? 'Ad gerekli' : 'Name is required';
    if (!formData.email) newErrors.email = locale === 'tr' ? 'E-posta gerekli' : 'Email is required';
    if (!formData.farmName) newErrors.farmName = locale === 'tr' ? 'Çiftlik adı gerekli' : 'Farm name is required';
    if (!formData.password) newErrors.password = locale === 'tr' ? 'Şifre gerekli' : 'Password is required';
    else if (formData.password.length < 8) newErrors.password = text.passwordTooShort;
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = text.passwordMismatch;
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsLoading(true);
    
    // Redirect to Django register endpoint
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${DJANGO_BASE_URL}/register/`;
    
    Object.entries(formData).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });
    
    document.body.appendChild(form);
    form.submit();
  };

  const handleGoogleSignUp = () => {
    window.location.href = `${DJANGO_BASE_URL}/auth/google/`;
  };

  return (
    <div className="min-h-screen bg-surface flex" data-testid="register-page">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 mb-8" data-testid="register-logo">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">AT</span>
            </div>
            <span className="text-xl font-bold tracking-tight text-primary">AgriTrack</span>
          </a>
          
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-black text-primary tracking-tight" data-testid="register-title">
                {text.createAccount}
              </h2>
              <p className="text-muted-foreground mt-2">{text.subtitle}</p>
            </div>
            
            {/* Google Sign Up */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 border-2 border-primary/10 hover:border-primary/20 hover:bg-primary/5 text-primary font-medium rounded-xl transition-all duration-200"
              onClick={handleGoogleSignUp}
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {text.googleSignUp}
            </Button>
            
            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-primary/10"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-surface px-4 text-muted-foreground font-mono tracking-wider">
                  {text.orContinueWith}
                </span>
              </div>
            </div>
            
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-primary font-medium">
                  {text.nameLabel}
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder={text.namePlaceholder}
                    value={formData.name}
                    onChange={handleChange}
                    className={`h-12 pl-12 rounded-xl border-2 ${errors.name ? 'border-red-500' : 'border-primary/10'} focus:border-primary focus:ring-primary`}
                    data-testid="name-input"
                  />
                </div>
                {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-primary font-medium">
                  {text.emailLabel}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={text.emailPlaceholder}
                    value={formData.email}
                    onChange={handleChange}
                    className={`h-12 pl-12 rounded-xl border-2 ${errors.email ? 'border-red-500' : 'border-primary/10'} focus:border-primary focus:ring-primary`}
                    data-testid="email-input"
                  />
                </div>
                {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="farmName" className="text-primary font-medium">
                  {text.farmLabel}
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="farmName"
                    name="farmName"
                    type="text"
                    placeholder={text.farmPlaceholder}
                    value={formData.farmName}
                    onChange={handleChange}
                    className={`h-12 pl-12 rounded-xl border-2 ${errors.farmName ? 'border-red-500' : 'border-primary/10'} focus:border-primary focus:ring-primary`}
                    data-testid="farm-input"
                  />
                </div>
                {errors.farmName && <p className="text-red-500 text-sm">{errors.farmName}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-primary font-medium">
                  {text.passwordLabel}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={text.passwordPlaceholder}
                    value={formData.password}
                    onChange={handleChange}
                    className={`h-12 pl-12 pr-12 rounded-xl border-2 ${errors.password ? 'border-red-500' : 'border-primary/10'} focus:border-primary focus:ring-primary`}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-primary font-medium">
                  {text.confirmPasswordLabel}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={text.confirmPasswordPlaceholder}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`h-12 pl-12 pr-12 rounded-xl border-2 ${errors.confirmPassword ? 'border-red-500' : 'border-primary/10'} focus:border-primary focus:ring-primary`}
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                    data-testid="toggle-confirm-password"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-sm">{errors.confirmPassword}</p>}
              </div>
              
              {/* Free tier badge */}
              <div className="flex items-center gap-2 p-3 bg-forest-50 rounded-xl border border-forest-100">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <p className="text-sm text-forest-700 font-medium">{text.freeForever}</p>
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-primary hover:bg-primary-light text-white font-semibold rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:hover:translate-y-0"
                data-testid="create-account-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {locale === 'tr' ? 'Oluşturuluyor...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    {text.createBtn}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              
              {/* Terms */}
              <p className="text-xs text-center text-muted-foreground">
                {text.terms}{' '}
                <a href={`${DJANGO_BASE_URL}/terms/`} className="text-primary hover:underline">
                  {text.termsLink}
                </a>{' '}
                {text.and}{' '}
                <a href={`${DJANGO_BASE_URL}/privacy/`} className="text-primary hover:underline">
                  {text.privacyLink}
                </a>
              </p>
            </form>
            
            {/* Sign in link */}
            <p className="text-center text-muted-foreground">
              {text.haveAccount}{' '}
              <a 
                href="/login" 
                className="text-primary hover:underline font-semibold"
                data-testid="signin-link"
              >
                {text.signIn}
              </a>
            </p>
          </div>
        </motion.div>
      </div>
      
      {/* Right Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary to-primary-light relative overflow-hidden">
        {/* Noise texture */}
        <div 
          className="absolute inset-0 opacity-[0.03]" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
          }} 
        />
        
        <div className="relative z-10 flex flex-col justify-center p-12 w-full">
          {/* Features list */}
          <div className="space-y-8">
            <h2 className="text-3xl xl:text-4xl font-black text-white leading-tight">
              {locale === 'tr' 
                ? 'Dakikalar içinde başlayın' 
                : 'Get started in minutes'}
            </h2>
            
            <div className="space-y-6">
              {[
                {
                  en: 'Track unlimited animals with the Pro plan',
                  tr: 'Pro planla sınırsız hayvan takibi',
                },
                {
                  en: 'Real-time growth analytics and insights',
                  tr: 'Gerçek zamanlı büyüme analitiği ve içgörüler',
                },
                {
                  en: 'Works offline in the field',
                  tr: 'Sahada çevrimdışı çalışır',
                },
                {
                  en: 'Bank-level data security',
                  tr: 'Banka düzeyinde veri güvenliği',
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.4 }}
                  className="flex items-center gap-4"
                >
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-forest-200 text-lg">{feature[locale] || feature.en}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-forest-400/10 rounded-full blur-3xl" />
      </div>
    </div>
  );
};

export default Register;
